import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { openUserForm } from 'services/forms';
import { OverflowMenu, OverflowMenuItem, Row, Column } from '@carbon/react';
import { Page } from 'components/Page';
import { CustomDataTable } from 'components/Datatable';
import {
  Panel,
  PanelFooter,
  PanelHeader,
  PanelLabel,
  PanelLink,
} from 'components/Panel';
import { CheckedItem, SearchBar } from 'components/SearchBar';
import { SettingsAdjust, User, UserRole } from '@carbon/react/icons';

import moment from 'moment';
import 'moment/locale/de';

import { useGetMeUsersQuery } from 'services/users';
import useModal from '@/lib/hooks/useModal';
import useFilter from '@/lib/hooks/useFilterSettings';
import { useGetMyUsersQuery } from '@/redux/new_services/users';

moment.locale('de');

const columns = [
  {
    id: 'icon',
    title: '',
  },
  {
    id: 'email',
    title: 'Email',
    sortCycle: 'bi-states-from-ascending',
  },
  {
    id: 'firstname',
    title: 'Vorname',
    sortCycle: 'tri-states-from-ascending',
  },
  {
    id: 'lastname',
    title: 'Nachname',
    sortCycle: 'tri-states-from-ascending',
  },
  {
    id: 'last_login',
    title: 'Letzte Anmeldung',
    sortCycle: 'tri-states-from-ascending',
  },
];

const getRowItems = (rows) => {
  return rows.map((row) => {
    const online = moment(row.last_login);
    const is_online = online.isAfter(moment().subtract(5, 'minutes'));
    let a = {
      icon:
        row.role.name === 'Employee' ? (
          <UserRole className={is_online ? 'online-state' : ""} />
        ) : (
          <User className={is_online ? 'online-state' : ""} />
        ),
      id: row.model.ID,
      email: row.email,
      firstname: row.firstname,
      lastname: row.lastname,
      last_login:
        row.last_login === '0001-01-01T00:00:00Z' ? '-' : online.fromNow(),
      selected: false,
    };

    return a;
  });
};

const RowActions = ({ row }) => {

  const [confirmRemoveUser] = useModal('DELETE_USER');
  const [openChangePassword] = useModal('CHANGE_PASSWORD');
  const dispatch = useDispatch();

  const handleEdit = (userID) => {
    dispatch(openUserForm(userID));
  };

  const delete_user = (user) => {
    confirmRemoveUser({ user })
  };

  return (
    <OverflowMenu flipped aria-label="Menü">
      <OverflowMenuItem
        itemText="Bearbeiten"
        onClick={() => {
          handleEdit(row.id);
        }}
      />
      <OverflowMenuItem
        itemText="Kennwort ändern"
        onClick={() => {
          openChangePassword({ user: { id: row.id, email: row.email } });
        }}
      />
      <OverflowMenuItem
        itemText="Löschen"
        onClick={() => {
          delete_user({ id: row.id, email: row.email });
        }}
      />
    </OverflowMenu>
  );
};

const UsersTable = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState({ columnId: 'name', direction: 'DESC' });
  const [search, setSearch] = useState(null);
  const [query, setQuery] = useState({pageIndex: page, search: search, order_by: sortBy, pageSize: pageSize});
  const {
    data = {data: {records: [], total_record: 0}},
    isFetching,
  } = useGetMyUsersQuery(query, {refetchOnMountOrArgChange: false});

  const [userFilter, setUserFilter] = useFilter();


  useEffect(() => {

    let filter = [];
    if (userFilter?.users?.role) {
      filter.push({ field: 'Role.name', value: userFilter.users.role });
    }

    setQuery(f => { return {...f, filters: filter, pageIndex: 1}});
  }, [userFilter]);

  const searchChanged = (text) => {
    setSearch(text);
  };

  useEffect(() => {
    if (sortBy && sortBy.length > 0) {
      const a = sortBy[0].split(' ');
      setSortBy({ sortId: a[0], direction: a[1] || 'ASC' });
    }
  }, [sortBy]);

  const rows = useMemo(
    () => getRowItems(data.data.records || []),
    [data]
  );

  return (
    <>
      <SearchBar searchChanged={searchChanged} labelText={"Benutzer suchen..."}>
        <OverflowMenu
          renderIcon={(props) => <SettingsAdjust size={32} {...props} />}
          flipped
          kind="secondary"
          size="lg"
          aria-label="Benutzer Filtern">
          <OverflowMenuItem
            itemText={
              <CheckedItem checked={userFilter?.users?.role === null} text="Alle Anzeigen" />
            }
            onClick={() => {
              setUserFilter(prev => ({...prev, users: {role: null}}));
            }}
          />
          <OverflowMenuItem
            itemText={
              <CheckedItem checked={userFilter?.users?.role === 'User'} text="Nur Benutzer" />
            }
            onClick={() => {
              setUserFilter(prev => ({...prev, users: {role: "User"}}));
            }}
          />
          <OverflowMenuItem
            itemText={
              <CheckedItem
                checked={userFilter?.users?.role === 'Employee'}
                text="Nur Mitbenutzer"
              />
            }
            onClick={() => {
              setUserFilter(prev => ({...prev, users: {role: "Employee"}}));
            }}
          />
        </OverflowMenu>
      </SearchBar>
      <CustomDataTable
        columns={columns.slice(
          0,
          columns.length
        )}
        search={search}
        rows={rows}
        sortInfo={sortBy}
        loadItems={({
          pageSize: newPageSize,
          pageIndex: newPageIndex,
          sortBy: newSortBy,
        }) => {
          setPageSize(newPageSize);
          setQuery(f => { return {...f, pageIndex: newPageIndex, search: search, sortBy: newSortBy, pageSize: newPageSize}});
          setPage(newPageIndex);
          if (newSortBy !== sortBy && newSortBy !== undefined) {
            setSortBy(newSortBy);
          }
        }}
        totalItems={data.data?.total_record || 0}
        pageSize={pageSize}
        page={page}
        hasExpandedRow={false}
        customRowActionsComponent={RowActions}
        isLoading={isFetching}
      />

      <br/>
      <br/>
    </>
  );
};

export const Users = () => {
  const dispatch = useDispatch();

  return (
    <Page
      pageClass="users-page"
      mainContentClass="users-page__r2"
      pageTitle="Benutzer Übersicht"
    >
      <Row>
        <Column md={5}>
          <Row className="small_row">
            <Column md={4}>
              <Panel halfSize>
                <PanelHeader header>
                  Benutzer zu micast einladen
                </PanelHeader>
                <PanelLabel>
                  Lade einen Benutzer zu micast ein. Dieser bekommt anschließend
                  eine Email
                </PanelLabel>
                <PanelFooter>
                  <PanelLink onClick={() => dispatch(openUserForm("new"))}>
                    BENUTZER HINZUFÜGEN &gt;
                  </PanelLink>
                </PanelFooter>
              </Panel>
            </Column>
          </Row>
        </Column>
      </Row>
      <Row>
        <Column>
          <UsersTable />
        </Column>
      </Row>
    </Page>
  );
};
